// Recibos.js - VERSÃO COMPLETA CORRIGIDA
// 🔥 CORREÇÃO: Ordenação da sequência (1/N = mais antigo, N/N = mais recente/final)
// 🔥 CORREÇÃO: Botão "Finalizar Pagamento" desaparece após finalizar
// 🔥 NOVO: Cadeia de Pagamentos (recibos irmãos unificados)
// 🔥 NOVO: Referência cruzada entre recibos da mesma fatura
// 🔥 NOVO: Badge "Parcial" / "Final" na coluna Status
// 🔥 NOVO: Sequência "1/2", "2/2" na coluna Nº
// 🔥 NOVO: Botão "Finalizar Pagamento" na linha expandida
// 🔥 CORREÇÃO: Recálculo defensivo do saldoRestante
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import './Recibos.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

// 🔥 STATUS DO RECIBO
const STATUS_RECIBO = [
    { value: 'Emitido', label: 'Emitido', color: '#10b981', icon: 'check_circle' },
    { value: 'Anulado', label: 'Anulado', color: '#dc2626', icon: 'cancel' },
];

// 🔥 MÉTODOS DE PAGAMENTO
const METODOS_PAGAMENTO = [
    { value: 'Dinheiro', label: 'Dinheiro', icon: 'payments' },
    { value: 'Transferência Bancária', label: 'Transferência Bancária', icon: 'account_balance' },
    { value: 'Cheque', label: 'Cheque', icon: 'receipt' },
    { value: 'Cartão', label: 'Cartão', icon: 'credit_card' },
    { value: 'M-Kesh', label: 'M-Kesh', icon: 'phone_android' },
    { value: 'M-Pesa', label: 'M-Pesa', icon: 'phone_android' },
    { value: 'E-Mola', label: 'E-Mola', icon: 'phone_android' },
    { value: 'Outro', label: 'Outro', icon: 'more_horiz' },
];

const ITEMS_PER_PAGE = 6;
const SEARCH_ITEMS_PER_PAGE = 5;

// =============================================
// 🔥 HELPER GLOBAL: Ordenação robusta de recibos
// Usado tanto na tabela como na CadeiaPagamentos
// Desempate: dataPagamento → createdAt → _id
// =============================================
const ordenarRecibosPorSequencia = (recibos) => {
    return [...recibos].sort((a, b) => {
        // 1º critério: data de pagamento (mais antiga primeiro)
        const dataA = new Date(a.dataPagamento || 0).getTime();
        const dataB = new Date(b.dataPagamento || 0).getTime();
        if (dataA !== dataB) return dataA - dataB;

        // 2º critério: createdAt (data de criação do registo)
        const createdA = new Date(a.createdAt || 0).getTime();
        const createdB = new Date(b.createdAt || 0).getTime();
        if (createdA !== createdB) return createdA - createdB;

        // 3º critério: _id (ObjectId é sequencial em MongoDB)
        const idA = String(a._id || '');
        const idB = String(b._id || '');
        return idA.localeCompare(idB);
    });
};

const getStatusInfo = (status) => {
    if (!status) return { value: 'Emitido', label: 'Emitido', color: '#10b981', icon: 'check_circle' };
    const found = STATUS_RECIBO.find(s => s.value === status);
    return found || { value: status, label: status, color: '#6b7280', icon: 'help' };
};

// =============================================
// COMPONENTE TOAST
// =============================================
function Toast({ message, type, onClose }) {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                onClose();
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message, onClose]);

    if (!message) return null;

    const icons = {
        success: 'check_circle',
        danger: 'error',
        warning: 'warning',
        info: 'info'
    };

    const colors = {
        success: '#10b981',
        danger: '#ef4444',
        warning: '#f59e0b',
        info: '#3b82f6'
    };

    return (
        <div className="toast-container" style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxWidth: '400px',
            width: '100%',
            pointerEvents: 'none'
        }}>
            <div className={`toast-message toast-${type}`} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colors[type] || '#6b7280'}`,
                pointerEvents: 'auto',
                animation: 'slideInRight 0.3s ease-out'
            }}>
                <Icon name={icons[type] || 'info'} size={20} style={{ color: colors[type] || '#6b7280', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.875rem', color: '#1a1a2e', fontWeight: 500, lineHeight: 1.4 }}>
                    {message}
                </span>
                <button onClick={onClose} style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    flexShrink: 0
                }}>
                    <Icon name="close" size={16} />
                </button>
            </div>
            <style>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(40px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
}

// =============================================
// COMPONENTE DE PAGINAÇÃO PARA ITENS
// =============================================
function ItensPagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);
        if (currentPage <= 3) { startPage = 2; endPage = Math.min(totalPages - 1, 4); }
        if (currentPage >= totalPages - 2) { startPage = Math.max(2, totalPages - 3); endPage = totalPages - 1; }
        if (startPage > 2) pages.push('...');
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="itens-pagination" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.25rem',
            padding: '0.5rem',
            borderTop: '1px solid #e5e7eb'
        }}>
            <button className="pagination-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
                <Icon name="chevron_left" size={14} />
            </button>
            {getPageNumbers().map((p, index) => {
                if (p === '...') return <span key={`dots-${index}`} className="pagination-dots">…</span>;
                return (
                    <button key={p} className={`pagination-number ${p === currentPage ? 'active' : ''}`} onClick={() => onPageChange(p)}>
                        {p}
                    </button>
                );
            })}
            <button className="pagination-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                <Icon name="chevron_right" size={14} />
            </button>
        </div>
    );
}

// =============================================
// COMPONENTE SEARCH INPUT (igual ao Fatura)
// =============================================
function SearchInput({
    value,
    onChange,
    onSelect,
    onClear,
    placeholder,
    items = [],
    getItemLabel,
    getItemSub,
    getItemTag,
    getItemPrice,
    getItemStatus,
    selectedId,
    iconName = 'search',
    noResultsMessage = 'Nenhum resultado encontrado',
    className = '',
    itemsPerPage = SEARCH_ITEMS_PER_PAGE,
    ...props
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [currentPage, setCurrentPage] = useState(1);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    const filteredItems = useMemo(() => {
        if (!items || !Array.isArray(items) || items.length === 0) return [];
        if (!searchTerm || searchTerm.trim() === '') return items;

        const search = searchTerm.toLowerCase().trim();

        return items.filter(item => {
            if (!item) return false;
            const searchableFields = [];
            if (getItemLabel) searchableFields.push(getItemLabel(item).toLowerCase());
            else if (item.nome) searchableFields.push(item.nome.toLowerCase());

            if (item.numero) searchableFields.push(String(item.numero).toLowerCase());
            if (item.nif) searchableFields.push(item.nif.toLowerCase());
            if (item.nuit) searchableFields.push(item.nuit.toLowerCase());
            if (item.email) searchableFields.push(item.email.toLowerCase());

            if (item.clienteInfo?.nome) searchableFields.push(item.clienteInfo.nome.toLowerCase());
            if (item.clienteId?.nome) searchableFields.push(item.clienteId.nome.toLowerCase());

            if (getItemStatus) {
                const st = getItemStatus(item);
                if (st) searchableFields.push(String(st).toLowerCase());
            }

            return searchableFields.some(f => f && f.includes(search));
        });
    }, [items, searchTerm, getItemLabel, getItemStatus]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    const handleSelect = (item) => {
        if (onSelect) onSelect(item);
        setIsOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const handleClear = () => {
        if (onClear) onClear();
        setSearchTerm('');
        setIsOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (onChange) onChange(val);
        setIsOpen(true);
        if (val === '' && onClear) onClear();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasValue = !!selectedId || searchTerm?.trim() !== '';

    const preventFormSubmit = (e) => { if (e) { e.preventDefault(); e.stopPropagation(); } };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        // 🔥 Gerar lista de páginas
        const pageNumbers = (() => {
            const pages = [];
            const total = totalPages;
            const atual = currentPage;

            // 🔥 Se tem 5 ou menos → mostra TODAS
            if (total <= 5) {
                for (let i = 1; i <= total; i++) {
                    pages.push(i);
                }
                return pages;
            }

            // 🔥 Senão → mostra com elipses
            pages.push(1);

            let inicio = Math.max(2, atual - 1);
            let fim = Math.min(total - 1, atual + 1);

            if (atual <= 3) {
                inicio = 2;
                fim = Math.min(total - 1, 4);
            }
            if (atual >= total - 2) {
                inicio = Math.max(2, total - 3);
                fim = total - 1;
            }

            if (inicio > 2) pages.push('...');

            for (let i = inicio; i <= fim; i++) {
                pages.push(i);
            }

            if (fim < total - 1) pages.push('...');

            pages.push(total);

            return pages;
        })();

        return (
            <div
                className="search-dropdown-pagination"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.15rem',
                    padding: '0.4rem 0.5rem',
                    borderTop: '1px solid #e5e7eb',
                    background: 'rgba(0,0,0,0.02)',
                    flexWrap: 'wrap'
                }}
                onMouseDown={preventFormSubmit}
                onClick={preventFormSubmit}
            >
                {/* Botão Anterior */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentPage(prev => Math.max(1, prev - 1));
                    }}
                    disabled={currentPage === 1}
                    style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '50%',
                        background: 'transparent',
                        color: '#1a1a2e',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: currentPage === 1 ? 0.4 : 1,
                        pointerEvents: currentPage === 1 ? 'none' : 'auto',
                        padding: 0
                    }}
                >
                    <Icon name="chevron_left" size={12} />
                </button>

                {/* Números das páginas */}
                {pageNumbers.map((p, index) => {
                    if (p === '...') {
                        return (
                            <span
                                key={`dots-${index}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '20px',
                                    height: '24px',
                                    color: '#6b7280',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    userSelect: 'none'
                                }}
                            >
                                …
                            </span>
                        );
                    }

                    const isActive = p === currentPage;

                    return (
                        <button
                            key={`page-${p}`}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCurrentPage(p);
                            }}
                            style={{
                                minWidth: '24px',
                                height: '24px',
                                padding: '0 0.15rem',
                                border: isActive ? '1px solid #4f46e5' : '1px solid transparent',
                                borderRadius: '50%',
                                background: isActive ? '#4f46e5' : 'transparent',
                                color: isActive ? '#ffffff' : '#1a1a2e',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.6rem',
                                fontWeight: isActive ? 700 : 500
                            }}
                        >
                            {p}
                        </button>
                    );
                })}

                {/* Botão Próximo */}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentPage(prev => Math.min(totalPages, prev + 1));
                    }}
                    disabled={currentPage === totalPages}
                    style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '50%',
                        background: 'transparent',
                        color: '#1a1a2e',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: currentPage === totalPages ? 0.4 : 1,
                        pointerEvents: currentPage === totalPages ? 'none' : 'auto',
                        padding: 0
                    }}
                >
                    <Icon name="chevron_right" size={12} />
                </button>

                {/* Contador */}
                <span style={{
                    fontSize: '0.55rem',
                    color: '#9ca3af',
                    marginLeft: '0.2rem'
                }}>
                    {filteredItems.length} itens
                </span>
            </div>
        );
    };

    return (
        <div className={`search-input-wrapper ${className}`} ref={wrapperRef}>
            <div className="search-input-inner">
                <Icon name={iconName} size={16} className={`search-icon ${hasValue ? 'has-value' : ''}`} />
                <input
                    ref={inputRef}
                    type="text"
                    className={`search-input-field ${hasValue ? 'has-value' : ''}`}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => setIsOpen(true)}
                    {...props}
                />
                {selectedId && (
                    <button type="button" className="search-clear-btn" onClick={handleClear} title="Limpar seleção">
                        <Icon name="close" size={14} />
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="search-dropdown">
                    {filteredItems.length === 0 ? (
                        <div className="search-dropdown-empty">
                            {items && items.length > 0 ? noResultsMessage : 'Carregando...'}
                        </div>
                    ) : (
                        <>
                            {paginatedItems.map((item, index) => (
                                <div key={item._id || index} className="search-dropdown-item" onClick={() => handleSelect(item)}>
                                    <div className="search-dropdown-item-info">
                                        <span className="item-name">{getItemLabel ? getItemLabel(item) : item.nome}</span>
                                        {getItemSub && getItemSub(item) && <span className="item-sub">{getItemSub(item)}</span>}
                                    </div>
                                    <div className="search-dropdown-item-actions">
                                        {getItemStatus && getItemStatus(item) && (
                                            <span className="item-tag" style={{ fontSize: '0.55rem', padding: '2px 8px', borderRadius: '100px', background: '#e5e7eb', color: '#6b7280', fontWeight: 600 }}>
                                                {getItemStatus(item)}
                                            </span>
                                        )}
                                        {getItemPrice && getItemPrice(item) && <span className="item-price">{getItemPrice(item)}</span>}
                                    </div>
                                </div>
                            ))}
                            {renderPagination()}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================
// 🔥 NOVO COMPONENTE: CADEIA DE PAGAMENTOS
// Mostra todos os recibos irmãos da mesma fatura
// 🔥 CORREÇÃO: Ordenação robusta (1/N = mais antigo, N/N = mais recente)
// =============================================
function CadeiaPagamentos({ reciboAtual, irmaos, formatarMoeda, formatarData, getValorPagoDisplay, getSaldoRestanteDisplay, onVerDetalhes, compact = false }) {
    if (!reciboAtual) return null;

    // 🔥 Combinar todos os recibos (irmãos + atual) e ordenar com critérios robustos
    // Isto garante que o 1/N seja sempre o mais antigo e o N/N o mais recente
    const todos = ordenarRecibosPorSequencia([
        ...irmaos.filter(r => r._id !== reciboAtual._id),
        reciboAtual
    ]);

    if (todos.length <= 1) return null;

    const totalPago = todos.reduce((acc, r) => acc + (parseFloat(r.valorPago) || 0), 0);
    const saldoRestanteAtual = parseFloat(reciboAtual.saldoRestante) || 0;
    const snap = reciboAtual.faturaSnapshot || {};
    const totalFatura = parseFloat(snap.total) || 0;

    // Padding para compact
    const padding = compact ? '8px 10px' : '10px 14px';
    const gap = compact ? '4px' : '6px';
    const fontSize = compact ? '0.5rem' : '0.6rem';
    const circleSize = compact ? '18px' : '22px';

    return (
        <div style={{
            padding: padding,
            background: 'rgba(79, 70, 229, 0.04)',
            border: '1px solid rgba(79, 70, 229, 0.15)',
            borderRadius: '8px',
            marginBottom: compact ? '8px' : '10px'
        }}>
            <div style={{
                fontSize: compact ? '0.55rem' : '0.6rem',
                fontWeight: 800,
                color: '#4f46e5',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: compact ? '6px' : '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                <Icon name="link" size={compact ? 10 : 12} />
                Cadeia de Pagamentos desta Fatura ({todos.length} {todos.length === 1 ? 'recibo' : 'recibos'})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: gap }}>
                {todos.map((r, idx) => {
                    const isAtual = r._id === reciboAtual._id;
                    const saldoR = parseFloat(r.saldoRestante) || 0;
                    const isFinal = saldoR <= 0.01 && totalFatura > 0;
                    const isAnulado = r.status === 'Anulado';

                    return (
                        <div
                            key={r._id}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: `${circleSize} 1fr auto auto`,
                                gap: compact ? '6px' : '8px',
                                alignItems: 'center',
                                padding: compact ? '5px 8px' : '6px 10px',
                                background: isAtual ? 'rgba(79, 70, 229, 0.08)' : '#ffffff',
                                border: isAtual ? '1.5px solid #4f46e5' : '1px solid #e5e7eb',
                                borderRadius: '6px',
                                fontSize: fontSize,
                                opacity: isAnulado ? 0.5 : 1
                            }}
                        >
                            <span style={{
                                width: circleSize,
                                height: circleSize,
                                borderRadius: '50%',
                                background: isAnulado ? '#9ca3af' : (isFinal ? '#059669' : (isAtual ? '#4f46e5' : '#e5e7eb')),
                                color: (isFinal || isAtual || isAnulado) ? '#fff' : '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: compact ? '0.5rem' : '0.55rem'
                            }}>
                                {isAnulado ? '×' : (idx + 1)}
                            </span>

                            <div style={{ overflow: 'hidden' }}>
                                <div style={{
                                    fontWeight: 700,
                                    color: '#1a1a2e',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    Recibo {r.numero}
                                    {isAtual && (
                                        <span style={{
                                            marginLeft: '6px',
                                            fontSize: compact ? '0.45rem' : '0.5rem',
                                            color: '#4f46e5',
                                            fontWeight: 700
                                        }}>
                                            (ATUAL)
                                        </span>
                                    )}
                                    {isFinal && !isAtual && !isAnulado && (
                                        <span style={{
                                            marginLeft: '6px',
                                            fontSize: compact ? '0.45rem' : '0.5rem',
                                            color: '#059669',
                                            fontWeight: 700
                                        }}>
                                            ✓ FINALIZOU
                                        </span>
                                    )}
                                    {isAnulado && (
                                        <span style={{
                                            marginLeft: '6px',
                                            fontSize: compact ? '0.45rem' : '0.5rem',
                                            color: '#dc2626',
                                            fontWeight: 700
                                        }}>
                                            ANULADO
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: compact ? '0.45rem' : '0.5rem', color: '#6b7280' }}>
                                    {formatarData(r.dataPagamento)} • {r.metodoPagamento || '---'}
                                </div>
                            </div>

                            <span style={{
                                fontWeight: 700,
                                color: isAnulado ? '#9ca3af' : '#059669',
                                textDecoration: isAnulado ? 'line-through' : 'none',
                                whiteSpace: 'nowrap'
                            }}>
                                {getValorPagoDisplay(r)}
                            </span>

                            {!isAtual && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onVerDetalhes) onVerDetalhes(r);
                                    }}
                                    title="Ver recibo"
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#4f46e5',
                                        padding: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Icon name="visibility" size={compact ? 12 : 14} />
                                </button>
                            )}
                            {isAtual && <span style={{ width: compact ? 12 : 14 }}></span>}
                        </div>
                    );
                })}
            </div>

            {/* Resumo */}
            <div style={{
                marginTop: compact ? '6px' : '8px',
                paddingTop: compact ? '6px' : '8px',
                borderTop: '1px dashed #c7d2fe',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '4px',
                fontSize: compact ? '0.5rem' : '0.55rem',
                color: '#4a5568'
            }}>
                <span>
                    Total pago: <strong style={{ color: '#059669' }}>
                        {formatarMoeda(totalPago)}
                    </strong>
                </span>
                <span>
                    Saldo restante: <strong style={{
                        color: saldoRestanteAtual > 0.01 ? '#d97706' : '#059669'
                    }}>
                        {formatarMoeda(saldoRestanteAtual)}
                    </strong>
                </span>
            </div>
        </div>
    );
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function Recibos() {
    // =============================================
    // ESTADOS PRINCIPAIS
    // =============================================
    const [recibos, setRecibos] = useState([]);
    const [filteredRecibos, setFilteredRecibos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [filtroMetodo, setFiltroMetodo] = useState('todos');

    const [expandedRows, setExpandedRows] = useState({});

    // =============================================
    // TOAST
    // =============================================
    const [toast, setToast] = useState({ message: '', type: '' });
    const showToast = (text, type = 'success') => setToast({ message: text, type });
    const closeToast = () => setToast({ message: '', type: '' });

    // =============================================
    // CONFIGURAÇÕES DA EMPRESA
    // =============================================
    const [empresaConfig, setEmpresaConfig] = useState({
        id: null,
        nome: '',
        nomeCurto: '',
        imagemPerfil: null,
        nuit: '',
        email: '',
        telefone: '',
        website: '',
        morada: {},
        dadosBancarios: [],
        bancoPrincipal: null,
        cafe: '',
        moeda: {
            simbolo: 'MT',
            codigo: 'MZN',
            casasDecimais: 2,
            separadorDecimal: ',',
            separadorMilhar: '.',
            posicaoSimbolo: 'depois',
            nome: 'Metical'
        },
        formatacao: {
            data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
            numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
        },
        modulos: {}
    });

    // =============================================
    // MODAIS
    // =============================================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAnularModal, setShowAnularModal] = useState(false);

    const [reciboToDelete, setReciboToDelete] = useState(null);
    const [reciboToAnular, setReciboToAnular] = useState(null);
    const [reciboDetalhes, setReciboDetalhes] = useState(null);
    const [motivoAnulacao, setMotivoAnulacao] = useState('');

    const [message, setMessage] = useState({ text: '', type: '' });

    const [empresaId, setEmpresaId] = useState(null);

    // =============================================
    // ESTATÍSTICAS
    // =============================================
    const [stats, setStats] = useState({
        total: 0,
        emitidos: 0,
        anulados: 0,
        totalValor: 0,
        totalValorFormatado: '0 MT'
    });

    // =============================================
    // PAGINAÇÃO
    // =============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 11,
        total: 0,
        pages: 0
    });

    // =============================================
    // FORM DATA - CRIAÇÃO
    // =============================================
    const [formData, setFormData] = useState({
        faturaId: '',
        valorPago: 0,
        metodoPagamento: 'Dinheiro',
        referenciaPagamento: '',
        dataPagamento: new Date().toISOString().split('T')[0],
        observacoes: ''
    });

    // =============================================
    // DADOS AUXILIARES
    // =============================================
    const [faturasPendentes, setFaturasPendentes] = useState([]);
    const [faturaSelecionada, setFaturaSelecionada] = useState(null);
    const [searchFatura, setSearchFatura] = useState('');
    const [saldoCalculado, setSaldoCalculado] = useState({
        totalFatura: 0,
        totalJaPago: 0,
        saldoDevedor: 0
    });

    // 🔥 Estado para modo "Finalizar Pagamento"
    const [modoFinalizar, setModoFinalizar] = useState(false);

    // 🔥 Guardar recibo que está a ser finalizado (para referência)
    const [reciboEmFinalizacao, setReciboEmFinalizacao] = useState(null);

    // =============================================
    // API INSTANCE
    // =============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
    });

    // =============================================
    // FORMATAR MOEDA
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais || 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // FORMATAR DATA
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || { formato: 'DD/MM/YYYY', separador: '/' };
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mes);
        formato = formato.replace('YYYY', ano);
        formato = formato.replace('YY', String(ano).slice(-2));

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // HELPERS
    // =============================================
    const getClienteNome = (recibo) => {
        if (!recibo) return 'N/A';
        if (recibo.clienteInfo?.nome) return recibo.clienteInfo.nome;
        if (recibo.clienteId?.nome) return recibo.clienteId.nome;
        if (recibo.faturaSnapshot?.cliente?.nome) return recibo.faturaSnapshot.cliente.nome;
        if (typeof recibo.clienteId === 'string') return 'Carregando...';
        return 'N/A';
    };

    const getClienteNif = (recibo) => {
        if (!recibo) return '';
        if (recibo.clienteInfo?.nif) return recibo.clienteInfo.nif;
        if (recibo.clienteId?.nif) return recibo.clienteId.nif;
        if (recibo.clienteId?.nuit) return recibo.clienteId.nuit;
        if (recibo.faturaSnapshot?.cliente?.nif) return recibo.faturaSnapshot.cliente.nif;
        return '';
    };

    const getFaturaNumero = (recibo) => {
        if (!recibo) return '-';
        if (recibo.faturaSnapshot?.numero) return recibo.faturaSnapshot.numero;
        if (recibo.faturaId?.numero) return recibo.faturaId.numero;
        return '-';
    };

    const getValorPagoDisplay = (recibo) => {
        if (recibo.valorPagoFormatado) return recibo.valorPagoFormatado;
        return formatarMoeda(recibo.valorPago || 0);
    };

    const getSaldoRestanteDisplay = (recibo) => {
        if (recibo.saldoRestanteFormatado) return recibo.saldoRestanteFormatado;
        return formatarMoeda(recibo.saldoRestante || 0);
    };

    const getStatusFormatado = (recibo) => recibo.statusFormatado || getStatusInfo(recibo.status).label;
    const getStatusClass = (recibo) => recibo.statusClass || (recibo.status || 'Emitido').toLowerCase();
    const getStatusIcon = (recibo) => recibo.statusIcon || getStatusInfo(recibo.status).icon;

    // 🔥 Cálculo de % de progresso correto
    const calcularPercentualPago = (recibo) => {
        const snap = recibo.faturaSnapshot || {};
        const totalFatura = parseFloat(snap.total) || 0;
        const saldoRestante = parseFloat(recibo.saldoRestante) || 0;
        if (totalFatura <= 0) return 0;
        const totalJaPago = Math.max(0, totalFatura - saldoRestante);
        return Math.min(100, (totalJaPago / totalFatura) * 100);
    };

    const calcularTotalJaPago = (recibo) => {
        const snap = recibo.faturaSnapshot || {};
        const totalFatura = parseFloat(snap.total) || 0;
        const saldoRestante = parseFloat(recibo.saldoRestante) || 0;
        return Math.max(0, totalFatura - saldoRestante);
    };

    // =============================================
    // 🔥 Verifica se um recibo pode ser finalizado
    // (parcial, não anulado, com saldo > 0 E fatura ainda não totalmente paga)
    // =============================================
    const podeFinalizarPagamento = useCallback((recibo) => {
        if (!recibo) return false;
        if (recibo.status === 'Anulado') return false;
        if (recibo.tipoPagamento === 'Total') return false;

        const saldoRestante = parseFloat(recibo.saldoRestante) || 0;

        // Se o saldo restante é zero ou negativo, a fatura está paga
        if (saldoRestante <= 0.01) return false;

        // Verificar se a fatura já está totalmente paga
        const snap = recibo.faturaSnapshot || {};
        const totalFatura = parseFloat(snap.total) || 0;
        if (totalFatura > 0) {
            const totalJaPago = Math.max(0, totalFatura - saldoRestante);
            if (totalJaPago >= totalFatura - 0.01) return false;
        }

        // 🔥 Verificação extra: se existe algum recibo irmão posterior que já finalizou
        const faturaId = recibo.faturaId?._id || recibo.faturaId;
        if (faturaId && Array.isArray(filteredRecibos)) {
            const irmaos = filteredRecibos.filter(r => {
                const rFaturaId = r.faturaId?._id || r.faturaId;
                return rFaturaId === faturaId &&
                    r._id !== recibo._id &&
                    r.status === 'Emitido' &&
                    (parseFloat(r.saldoRestante) || 0) <= 0.01;
            });
            // Se algum irmão já finalizou, este recibo não pode ser finalizado
            if (irmaos.length > 0) return false;
        }

        return true;
    }, [filteredRecibos]);

    // =============================================
    // 🔥 CORREÇÃO: Recibos da fatura ordenados corretamente
    // Ordenação: dataPagamento → createdAt → _id
    // Isto garante que o 1/N seja sempre o MAIS ANTIGO
    // e o N/N o MAIS RECENTE (que finalizou)
    // =============================================
    const getRecibosDaFatura = useCallback((faturaId) => {
        if (!faturaId) return [];

        const recibos = filteredRecibos.filter(r => {
            const rFaturaId = r.faturaId?._id || r.faturaId;
            return rFaturaId === faturaId && r.status !== 'Anulado';
        });

        // 🔥 Usar helper global de ordenação
        return ordenarRecibosPorSequencia(recibos);
    }, [filteredRecibos]);

    // =============================================
    // 🔥 Sequência do recibo na fatura (1/N, 2/N, ..., N/N)
    // 1/N = mais antigo | N/N = mais recente (final)
    // =============================================
    const getSequenciaRecibo = useCallback((recibo) => {
        const faturaId = recibo.faturaId?._id || recibo.faturaId;
        if (!faturaId) return null;
        const irmaos = getRecibosDaFatura(faturaId);
        if (irmaos.length <= 1) return null;
        const posicao = irmaos.findIndex(r => r._id === recibo._id) + 1;
        if (posicao <= 0) return null;
        return `${posicao}/${irmaos.length}`;
    }, [getRecibosDaFatura]);

    // 🔥 Obter recibos irmãos (mesma fatura, exceto o atual)
    const getRecibosIrmaos = useCallback((recibo) => {
        if (!recibo) return [];
        const faturaId = recibo.faturaId?._id || recibo.faturaId;
        if (!faturaId) return [];
        return getRecibosDaFatura(faturaId).filter(r => r._id !== recibo._id);
    }, [getRecibosDaFatura]);

    // =============================================
    // BUSCAR DADOS
    // =============================================
    const fetchFaturasPendentes = async () => {
        try {
            console.log('📄 Buscando faturas pendentes...');
            const response = await api.get('/faturas?limit=500');

            if (response.data && response.data.data) {
                const faturas = response.data.data.filter(f =>
                    ['Aberta', 'Emitida', 'Vencida'].includes(f.status)
                );
                console.log(`📄 ${faturas.length} faturas pendentes`);
                setFaturasPendentes(faturas);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar faturas:', error);
        }
    };

    const fetchSaldoFatura = async (faturaId) => {
        if (!faturaId) return;
        try {
            const response = await api.get(`/recibos/fatura/${faturaId}`);
            if (response.data && response.data.success) {
                const totalFatura = response.data.fatura?.total || 0;
                const totalPago = response.data.totalPago || 0;
                const saldoDevedor = Math.max(0, totalFatura - totalPago);

                setSaldoCalculado({
                    totalFatura,
                    totalJaPago: totalPago,
                    saldoDevedor
                });

                return { totalFatura, totalPago, saldoDevedor };
            }
        } catch (error) {
            console.error('❌ Erro ao buscar saldo da fatura:', error);
        }
        return null;
    };

    const fetchRecibos = async (showLoading = true, pageOverride = null) => {
        if (showLoading) setTableLoading(true);
        setLoading(true);

        const currentPage = pageOverride !== null ? pageOverride : pagination.page;

        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filtroStatus && filtroStatus !== 'todos') params.append('status', filtroStatus);
            if (filtroMetodo && filtroMetodo !== 'todos') params.append('metodoPagamento', filtroMetodo);
            params.append('page', currentPage);
            params.append('limit', pagination.limit);

            console.log(`📄 Buscando página ${currentPage}...`);

            const response = await api.get(`/recibos?${params.toString()}`);

            if (response.data) {
                if (response.data.empresa) {
                    const empresa = response.data.empresa;
                    const morada = empresa.morada || {};
                    const enderecoCompleto = [
                        morada.rua, morada.numero, morada.bairro, morada.cidade, morada.provincia, morada.pais
                    ].filter(Boolean).join(', ');

                    setEmpresaConfig(prev => ({
                        ...prev,
                        id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        email: empresa.email || '',
                        telefone: empresa.telefone || '',
                        website: empresa.website || '',
                        morada: { ...morada, enderecoCompleto },
                        dadosBancarios: empresa.dadosBancarios || [],
                        bancoPrincipal: empresa.bancoPrincipal || null,
                        cafe: empresa.cafe || '',
                        moeda: empresa.moeda || prev.moeda,
                        formatacao: empresa.formatacao || prev.formatacao,
                        modulos: empresa.modulos || prev.modulos
                    }));
                }

                if (response.data.data && Array.isArray(response.data.data)) {
                    setRecibos(response.data.data);
                    setFilteredRecibos(response.data.data);

                    if (response.data.pagination) {
                        setPagination({ ...response.data.pagination, page: currentPage });
                    }

                    if (response.data.resumo) {
                        const resumo = response.data.resumo;
                        const statsData = {
                            total: response.data.pagination?.total || 0,
                            emitidos: 0,
                            anulados: 0,
                            totalValor: 0,
                            totalValorFormatado: '0 MT'
                        };

                        resumo.forEach(item => {
                            if (item.status === 'Emitido') {
                                statsData.emitidos = item.count;
                                statsData.totalValor = item.total || 0;
                                statsData.totalValorFormatado = item.totalFormatado || '0 MT';
                            }
                            if (item.status === 'Anulado') statsData.anulados = item.count;
                        });

                        setStats(statsData);
                    }
                } else {
                    setRecibos([]);
                    setFilteredRecibos([]);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao carregar recibos:', error);
            showMessage('Erro ao carregar recibos', 'danger');
            setRecibos([]);
            setFilteredRecibos([]);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    // =============================================
    // CRIAR RECIBO
    // =============================================
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!formData.faturaId) {
            showToast('Selecione uma fatura', 'danger');
            setLoading(false);
            return;
        }

        if (!formData.valorPago || formData.valorPago <= 0) {
            showToast('Informe o valor pago', 'danger');
            setLoading(false);
            return;
        }

        if (formData.valorPago > saldoCalculado.saldoDevedor + 0.01) {
            showToast(`Valor pago excede o saldo devedor (${formatarMoeda(saldoCalculado.saldoDevedor)})`, 'danger');
            setLoading(false);
            return;
        }

        if (formData.metodoPagamento !== 'Dinheiro' && !formData.referenciaPagamento.trim()) {
            showToast('Informe a referência do pagamento', 'warning');
            setLoading(false);
            return;
        }

        try {
            const dadosEnvio = {
                faturaId: formData.faturaId,
                valorPago: parseFloat(formData.valorPago),
                metodoPagamento: formData.metodoPagamento,
                referenciaPagamento: formData.referenciaPagamento,
                dataPagamento: formData.dataPagamento,
                observacoes: formData.observacoes
            };

            // 🔥 Se estamos a finalizar, adicionar referência ao recibo anterior
            if (modoFinalizar && reciboEmFinalizacao) {
                dadosEnvio.reciboAnteriorId = reciboEmFinalizacao._id;
                dadosEnvio.reciboAnteriorNumero = reciboEmFinalizacao.numero;
            }

            console.log('📤 Enviando dados para API:', dadosEnvio);

            const response = await api.post('/recibos', dadosEnvio);

            if (response.data) {
                const tipoMsg = modoFinalizar ? 'Pagamento finalizado' : 'Recibo criado';
                showToast(`${tipoMsg} com sucesso!`, 'success');
                setShowCreateModal(false);
                setModoFinalizar(false);
                setReciboEmFinalizacao(null);
                resetForm();

                // 🔥 Limpar estados expandidos e forçar recarregamento completo
                setExpandedRows({});

                // 🔥 Recarregar com delay para garantir que o backend processou
                await new Promise(resolve => setTimeout(resolve, 300));
                await fetchRecibos(true, pagination.page);
                await fetchFaturasPendentes();

                // 🔥 Disparar evento global
                window.dispatchEvent(new CustomEvent('recibo-criado', {
                    detail: {
                        recibo: response.data.data || response.data,
                        finalizou: modoFinalizar
                    }
                }));
            }
        } catch (error) {
            console.error('❌ Erro ao criar recibo:', error);
            showToast(error.response?.data?.erro || 'Erro ao criar recibo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // 🔥 FINALIZAR PAGAMENTO DE RECIBO PARCIAL
    // Abre o modal já com a fatura + valor + método pré-preenchidos
    // =============================================
    const finalizarPagamento = (recibo) => {
        if (!recibo) return;

        const faturaId = recibo.faturaId?._id || recibo.faturaId;
        const snap = recibo.faturaSnapshot || {};
        const saldoRestante = parseFloat(recibo.saldoRestante) || 0;
        const clienteSnapshot = snap.cliente || {};

        // 🔥 VALIDAÇÃO: Não abrir se não há saldo
        if (saldoRestante <= 0.01) {
            showToast('Esta fatura já está totalmente paga', 'info');
            return;
        }

        // Construir objeto mínimo da fatura para o modal
        const faturaParaModal = {
            _id: faturaId,
            numero: snap.numero || '',
            total: snap.total || 0,
            totalFormatado: formatarMoeda(snap.total || 0),
            status: 'Emitida',
            clienteInfo: clienteSnapshot,
            clienteId: clienteSnapshot
        };

        // Pré-preencher o form
        setFaturaSelecionada(faturaParaModal);
        setSearchFatura(faturaParaModal.numero);
        setFormData({
            faturaId: faturaId,
            valorPago: saldoRestante,
            metodoPagamento: recibo.metodoPagamento || 'Dinheiro',
            referenciaPagamento: '',
            dataPagamento: new Date().toISOString().split('T')[0],
            observacoes: `Pagamento final da fatura ${faturaParaModal.numero} (ref. recibo ${recibo.numero})`
        });

        // Ativar modo finalizar e guardar recibo de referência
        setModoFinalizar(true);
        setReciboEmFinalizacao(recibo);

        // Buscar saldo real (por segurança)
        fetchSaldoFatura(faturaId);

        // Abrir modal
        setShowCreateModal(true);
    };

    // =============================================
    // ANULAR RECIBO
    // =============================================
    const anularRecibo = async (id) => {
        if (!motivoAnulacao.trim()) {
            showToast('Informe o motivo da anulação', 'warning');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post(`/recibos/${id}/anular`, {
                motivo: motivoAnulacao
            });

            if (response.data) {
                showToast('Recibo anulado com sucesso!', 'success');

                const stockReposto = response.data.stockReposto || [];
                if (stockReposto.length > 0) {
                    const resumoStock = stockReposto
                        .map(s => `${s.produtoNome} (+${s.quantidade})`)
                        .join(', ');

                    setTimeout(() => {
                        showToast(`📦 Stock reposto: ${resumoStock}`, 'info');
                    }, 600);
                }

                const faturaRevertida = response.data.data?.faturaId?.status === 'Emitida';
                if (faturaRevertida) {
                    setTimeout(() => {
                        showToast(`🔄 Fatura ${response.data.data.faturaId.numero} revertida para "Emitida"`, 'warning');
                    }, 1200);
                }

                setShowAnularModal(false);
                setReciboToAnular(null);
                setMotivoAnulacao('');

                // 🔥 Limpar estados expandidos
                setExpandedRows({});

                await fetchRecibos(true, pagination.page);

                if (reciboDetalhes && reciboDetalhes._id === id) {
                    setReciboDetalhes(response.data.data);
                }

                window.dispatchEvent(new CustomEvent('recibo-anulado', {
                    detail: {
                        recibo: response.data.data,
                        faturaId: response.data.data?.faturaId?._id || response.data.data?.faturaId,
                        stockReposto: stockReposto
                    }
                }));
            }
        } catch (error) {
            console.error('❌ Erro ao anular recibo:', error);
            showToast(error.response?.data?.erro || 'Erro ao anular recibo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // EXCLUIR RECIBO
    // =============================================
    const handleDelete = async () => {
        if (!reciboToDelete) return;
        setLoading(true);

        try {
            const response = await api.delete(`/recibos/${reciboToDelete._id}`);
            if (response.data) {
                showToast('Recibo removido com sucesso!', 'success');
                setShowDeleteModal(false);
                setReciboToDelete(null);
                setExpandedRows({});
                await fetchRecibos(true, pagination.page);
            }
        } catch (error) {
            console.error('❌ Erro ao remover recibo:', error);
            showToast(error.response?.data?.erro || 'Erro ao remover recibo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // VER DETALHES
    // =============================================
    const verDetalhes = async (recibo) => {
        setLoading(true);
        try {
            console.log('🔍 Buscando detalhes do recibo ID:', recibo._id);

            const response = await api.get(`/recibos/${recibo._id}`);

            if (response.data) {
                const dadosRecibo = response.data.data || response.data;

                if (response.data.empresa) {
                    const empresa = response.data.empresa;
                    const morada = empresa.morada || {};
                    const enderecoCompleto = [
                        morada.rua, morada.numero, morada.bairro, morada.cidade, morada.provincia, morada.pais
                    ].filter(Boolean).join(', ');

                    setEmpresaConfig(prev => ({
                        ...prev,
                        id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        email: empresa.email || '',
                        telefone: empresa.telefone || '',
                        website: empresa.website || '',
                        morada: { ...morada, enderecoCompleto },
                        dadosBancarios: empresa.dadosBancarios || [],
                        bancoPrincipal: empresa.bancoPrincipal || null,
                        cafe: empresa.cafe || '',
                        moeda: empresa.moeda || prev.moeda,
                        formatacao: empresa.formatacao || prev.formatacao,
                        modulos: empresa.modulos || prev.modulos
                    }));

                    dadosRecibo.empresa = {
                        _id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        email: empresa.email || '',
                        telefone: empresa.telefone || '',
                        website: empresa.website || '',
                        morada: empresa.morada || {},
                        dadosBancarios: empresa.dadosBancarios || [],
                        bancoPrincipal: empresa.bancoPrincipal || null,
                        cafe: empresa.cafe || ''
                    };
                }

                setReciboDetalhes(dadosRecibo);
                setShowDetailsModal(true);
            }
        } catch (error) {
            console.error('❌ Erro ao buscar detalhes:', error);
            showToast('Erro ao carregar detalhes', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // TOGGLE EXPANDIR LINHA
    // =============================================
    const toggleExpandRow = (reciboId) => {
        setExpandedRows(prev => ({ ...prev, [reciboId]: !prev[reciboId] }));
    };

    // =============================================
    // RESET FORM
    // =============================================
    const resetForm = () => {
        setFormData({
            faturaId: '',
            valorPago: 0,
            metodoPagamento: 'Dinheiro',
            referenciaPagamento: '',
            dataPagamento: new Date().toISOString().split('T')[0],
            observacoes: ''
        });
        setFaturaSelecionada(null);
        setSearchFatura('');
        setSaldoCalculado({ totalFatura: 0, totalJaPago: 0, saldoDevedor: 0 });
        setModoFinalizar(false);
        setReciboEmFinalizacao(null);
    };

    // =============================================
    // SELEÇÃO DE FATURA
    // =============================================
    const selecionarFatura = async (fatura) => {
        if (!fatura) return;

        setFaturaSelecionada(fatura);
        setSearchFatura(fatura.numero);
        setFormData(prev => ({
            ...prev,
            faturaId: fatura._id,
            valorPago: 0
        }));

        await fetchSaldoFatura(fatura._id);
    };

    const limparSelecaoFatura = () => {
        setFaturaSelecionada(null);
        setSearchFatura('');
        setFormData(prev => ({ ...prev, faturaId: '', valorPago: 0 }));
        setSaldoCalculado({ totalFatura: 0, totalJaPago: 0, saldoDevedor: 0 });
        setModoFinalizar(false);
        setReciboEmFinalizacao(null);
    };

    // =============================================
    // PAGAR TOTALIDADE
    // =============================================
    const pagarTotalidade = () => {
        if (saldoCalculado.saldoDevedor > 0) {
            setFormData(prev => ({ ...prev, valorPago: saldoCalculado.saldoDevedor }));
        }
    };

    // =============================================
    // MENSAGENS
    // =============================================
    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    // =============================================
    // FILTROS
    // =============================================
    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchRecibos(true, 1);
    };

    const limparFiltros = () => {
        setSearchTerm('');
        setFiltroStatus('todos');
        setFiltroMetodo('todos');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => fetchRecibos(true, 1), 100);
    };

    // =============================================
    // PAGINAÇÃO
    // =============================================
    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
        fetchRecibos(true, page);
    };

    // =============================================
    // USE EFFECTS
    // =============================================
    useEffect(() => {
        const carregarDados = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (user.empresaId) setEmpresaId(user.empresaId);
                else if (user.empresa?._id) setEmpresaId(user.empresa._id);
            } catch (e) {
                console.warn('⚠️ Não foi possível obter empresaId');
            }

            await Promise.all([
                fetchFaturasPendentes(),
                fetchRecibos(true, 1)
            ]);
        };
        carregarDados();
    }, []);

    // =============================================
    // 🔥 ESCUTAR EVENTO GLOBAL DE RECIBO CRIADO
    // =============================================
    useEffect(() => {
        const handleReciboCriado = (event) => {
            console.log('🔄 Evento recibo-criado recebido:', event.detail);

            const recibo = event.detail?.recibo;
            const stock = event.detail?.stockAtualizado || [];

            if (recibo) {
                console.log(`✅ Recibo ${recibo.numero} criado automaticamente, recarregando lista...`);
                showToast(`📄 Novo recibo ${recibo.numero} adicionado automaticamente!`, 'success');
            }

            if (stock.length > 0) {
                console.log(`📦 Stock atualizado: ${stock.length} produtos`);
            }

            fetchRecibos(true, pagination.page);
            fetchFaturasPendentes();
        };

        window.addEventListener('recibo-criado', handleReciboCriado);

        return () => {
            window.removeEventListener('recibo-criado', handleReciboCriado);
        };
    }, [pagination.page]);

    // =============================================
    // RENDER PAGINAÇÃO
    // =============================================
    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        const getPageNumbers = (currentPage, totalPages) => {
            const arr = [];
            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) arr.push(i);
                return arr;
            }
            arr.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) { startPage = 2; endPage = Math.min(totalPages - 1, 4); }
            if (currentPage >= totalPages - 2) { startPage = Math.max(2, totalPages - 3); endPage = totalPages - 1; }
            if (startPage > 2) arr.push('...');
            for (let i = startPage; i <= endPage; i++) arr.push(i);
            if (endPage < totalPages - 1) arr.push('...');
            arr.push(totalPages);
            return arr;
        };

        const pageNumbers = getPageNumbers(page, pages);

        return (
            <div className="cotacoes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} recibos</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') return <span key={`dots-${index}`} className="pagination-dots">…</span>;
                            return (
                                <button key={p} className={`pagination-number ${page === p ? 'active' : ''}`} onClick={() => goToPage(p)}>
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button className="pagination-btn" onClick={() => goToPage(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // =============================================
    // RENDER PRINCIPAL
    // =============================================
    return (
        <div className="cotacoes-container recibos-container">
            {/* HEADER */}
            <div className="cotacoes-header">
                <div className="header-title">
                    <h2><Icon name="receipt_long" size={24} /> Recibos</h2>
                    <p>Gerencie os recibos de pagamento</p>
                </div>
                <div className="header-actions">
                    <button className="btn-refresh" onClick={() => fetchRecibos(true)} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                    <button className="btn-create" onClick={() => {
                        resetForm();
                        fetchFaturasPendentes();
                        setShowCreateModal(true);
                    }}>
                        <Icon name="add" size={18} /> Novo Recibo
                    </button>
                </div>
            </div>

            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* KPIs */}
            <div className="cotacoes-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="receipt_long" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.total}</span>
                        <span className="kpi-label">Total Recibos</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.emitidos}</span>
                        <span className="kpi-label">Emitidos</span>
                    </div>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="cancel" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.anulados}</span>
                        <span className="kpi-label">Anulados</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="payments" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value" style={{ fontSize: '0.85rem' }}>{stats.totalValorFormatado}</span>
                        <span className="kpi-label">Valor Recebido</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="cotacoes-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar recibos (nº, cliente, referência)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && aplicarFiltros()}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <select className="filter-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                        <option value="todos">Todos os Status</option>
                        {STATUS_RECIBO.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                    <select className="filter-select" value={filtroMetodo} onChange={(e) => setFiltroMetodo(e.target.value)}>
                        <option value="todos">Todos os Métodos</option>
                        {METODOS_PAGAMENTO.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                    <button className="btn-filter-apply" onClick={aplicarFiltros}>
                        <Icon name="search" size={16} /> Aplicar
                    </button>
                    <button className="btn-filter-clear" onClick={limparFiltros}>
                        <Icon name="clear" size={16} />
                    </button>
                </div>
            </div>

            {/* TABELA */}
            <div className="cotacoes-table-container">
                {tableLoading && (
                    <div className="table-loading-overlay active">
                        <div className="spinner"></div>
                        <span className="loading-text">A carregar recibos...</span>
                    </div>
                )}

                <div className="cotacoes-table-wrapper">
                    <table className="cotacoes-table">
                        <thead>
                            <tr>
                                <th className="col-expand" style={{ width: '40px', textAlign: 'center' }}></th>
                                <th className="col-numero">Nº</th>
                                <th className="col-cliente">Cliente</th>
                                <th className="col-fatura" style={{ textAlign: 'center' }}>Fatura</th>
                                <th className="col-metodo" style={{ textAlign: 'center' }}>Método</th>
                                <th className="col-valor" style={{ textAlign: 'right' }}>Valor Pago</th>
                                <th className="col-saldo" style={{ textAlign: 'right' }}>Saldo</th>
                                <th className="col-data" style={{ textAlign: 'center' }}>Data</th>
                                <th className="col-status" style={{ textAlign: 'center' }}>Status</th>
                                <th className="col-acoes" style={{ textAlign: 'center' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !tableLoading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <tr key={`skeleton-${index}`} className="skeleton-row">
                                        <td><div className="skeleton-line" style={{ width: '20%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '40%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '60%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '50%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '50%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '40%' }}></div></td>
                                        <td><div className="skeleton-badge"></div></td>
                                        <td>
                                            <div className="skeleton-actions">
                                                <div className="skeleton-circle small"></div>
                                                <div className="skeleton-circle small"></div>
                                                <div className="skeleton-circle small"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : filteredRecibos.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="10">
                                        <div className="empty-state">
                                            <Icon name="receipt_long" size={48} />
                                            <p>Nenhum recibo encontrado</p>
                                            <button className="btn-create-small" onClick={() => { resetForm(); fetchFaturasPendentes(); setShowCreateModal(true); }}>
                                                <Icon name="add" size={16} /> Criar primeiro recibo
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredRecibos.map(recibo => {
                                    const statusValue = recibo.status || 'Emitido';
                                    const isExpanded = expandedRows[recibo._id] || false;

                                    const podeAnular = statusValue === 'Emitido';
                                    const podeExcluir = statusValue === 'Anulado';
                                    const podeFinalizar = podeFinalizarPagamento(recibo);

                                    const valorPago = getValorPagoDisplay(recibo);
                                    const saldoRestante = getSaldoRestanteDisplay(recibo);
                                    const statusDisplay = getStatusFormatado(recibo);
                                    const statusClass = getStatusClass(recibo);
                                    const statusIcon = getStatusIcon(recibo);

                                    const nomeCliente = getClienteNome(recibo);
                                    const nifCliente = getClienteNif(recibo);
                                    const faturaNumero = getFaturaNumero(recibo);

                                    const snap = recibo.faturaSnapshot || {};
                                    const totalFatura = snap.total || 0;
                                    const saldoAnterior = recibo.saldoAnterior || 0;

                                    const totalJaPago = calcularTotalJaPago(recibo);
                                    const percentualPago = calcularPercentualPago(recibo);

                                    // 🔥 Sequência do recibo na fatura (1/N, 2/N, ..., N/N)
                                    const sequencia = getSequenciaRecibo(recibo);

                                    // 🔥 Indica se é parcial
                                    const isParcial = recibo.tipoPagamento === 'Parcial' && statusValue !== 'Anulado';

                                    // 🔥 Obter irmãos para cadeia de pagamentos
                                    const irmaos = getRecibosIrmaos(recibo);

                                    return (
                                        <React.Fragment key={recibo._id}>
                                            <tr className={`cotacao-row ${statusValue === 'Anulado' ? 'cancelled-row' : ''} ${isParcial ? 'parcial-row' : ''} ${isExpanded ? 'expanded' : ''}`}>
                                                <td className="col-expand" style={{ textAlign: 'center' }}>
                                                    <button
                                                        className="btn-expand-row"
                                                        onClick={() => toggleExpandRow(recibo._id)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#6b7280',
                                                            transition: 'transform 0.3s ease',
                                                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon name="chevron_right" size={18} />
                                                    </button>
                                                </td>
                                                <td className="col-numero">
                                                    <span className="numero-badge">{recibo.numero}</span>
                                                    {/* 🔥 Sequência 1/N, 2/N, ..., N/N */}
                                                    {sequencia && (
                                                        <span style={{
                                                            display: 'block',
                                                            fontSize: '0.45rem',
                                                            color: '#6b7280',
                                                            marginTop: '2px',
                                                            fontWeight: 600,
                                                            letterSpacing: '0.3px'
                                                        }}>
                                                            {sequencia}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="col-cliente">
                                                    <div className="cliente-info">
                                                        <strong>{nomeCliente}</strong>
                                                        {nifCliente && <span className="nif">NUIT: {nifCliente}</span>}
                                                    </div>
                                                </td>
                                                <td className="col-fatura" style={{ textAlign: 'center' }}>
                                                    <span className="numero-badge" style={{ fontSize: '0.5rem' }}>{faturaNumero}</span>
                                                </td>
                                                <td className="col-metodo" style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        fontSize: '0.55rem',
                                                        padding: '2px 8px',
                                                        borderRadius: '100px',
                                                        background: 'rgba(79, 70, 229, 0.08)',
                                                        color: '#4f46e5',
                                                        fontWeight: 600
                                                    }}>
                                                        {recibo.metodoPagamento || '---'}
                                                    </span>
                                                </td>
                                                <td className="col-valor" style={{ textAlign: 'right' }}>
                                                    <strong style={{ color: '#059669', fontSize: '0.6rem' }}>{valorPago}</strong>
                                                </td>
                                                <td className="col-saldo" style={{ textAlign: 'right' }}>
                                                    <span style={{
                                                        fontSize: '0.55rem',
                                                        fontWeight: 600,
                                                        color: (parseFloat(recibo.saldoRestante) || 0) > 0.01 ? '#d97706' : '#059669'
                                                    }}>
                                                        {saldoRestante}
                                                    </span>
                                                </td>
                                                <td className="col-data" style={{ textAlign: 'center' }}>
                                                    <span className="validade-badge">{formatarData(recibo.dataPagamento)}</span>
                                                </td>
                                                <td className="col-status" style={{ textAlign: 'center' }}>
                                                    <span className={`status-badge ${statusClass}`}>
                                                        <Icon name={statusIcon} size={12} />
                                                        {statusDisplay}
                                                    </span>
                                                    {/* 🔥 Badge "Parcial" */}
                                                    {isParcial && (parseFloat(recibo.saldoRestante) || 0) > 0.01 && (
                                                        <span style={{
                                                            display: 'block',
                                                            fontSize: '0.45rem',
                                                            color: '#f59e0b',
                                                            fontWeight: 700,
                                                            marginTop: '2px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Parcial
                                                        </span>
                                                    )}
                                                    {/* 🔥 Badge "Final" quando saldo = 0 */}
                                                    {!isParcial && statusValue === 'Emitido' && (parseFloat(recibo.saldoRestante) || 0) <= 0.01 && totalFatura > 0 && (
                                                        <span style={{
                                                            display: 'block',
                                                            fontSize: '0.45rem',
                                                            color: '#059669',
                                                            fontWeight: 700,
                                                            marginTop: '2px',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            Final
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="col-acoes" style={{ textAlign: 'center' }}>
                                                    <div className="actions-cell">
                                                        <button className="action-btn view" onClick={() => verDetalhes(recibo)} title="Ver detalhes">
                                                            <Icon name="visibility" size={16} />
                                                        </button>
                                                        {/* 🔥 BOTÃO FINALIZAR PAGAMENTO */}
                                                        {podeFinalizar && (
                                                            <button
                                                                className="action-btn finalize"
                                                                onClick={() => finalizarPagamento(recibo)}
                                                                title="Finalizar Pagamento"
                                                                style={{ color: '#f59e0b' }}
                                                            >
                                                                <Icon name="task_alt" size={16} />
                                                            </button>
                                                        )}
                                                        {podeAnular && (
                                                            <button className="action-btn cancel" onClick={() => {
                                                                setReciboToAnular(recibo);
                                                                setMotivoAnulacao('');
                                                                setShowAnularModal(true);
                                                            }} title="Anular">
                                                                <Icon name="cancel" size={16} />
                                                            </button>
                                                        )}
                                                        {podeExcluir && (
                                                            <button className="action-btn delete" onClick={() => {
                                                                setReciboToDelete(recibo);
                                                                setShowDeleteModal(true);
                                                            }} title="Excluir">
                                                                <Icon name="delete" size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="expanded-details-row">
                                                    <td colSpan="10" style={{ padding: 0 }}>
                                                        <div className="expanded-itens-container">
                                                            <div className="expanded-itens-header">
                                                                <span className="expanded-itens-title">
                                                                    <Icon name="info" size={14} /> Detalhes do Pagamento
                                                                </span>
                                                                <span className="expanded-itens-count">
                                                                    {recibo.tipoPagamento === 'Total' ? '✓ Pagamento Total' : '⏳ Pagamento Parcial'}
                                                                </span>
                                                            </div>

                                                            {/* 🔥 CADEIA DE PAGAMENTOS */}
                                                            <CadeiaPagamentos
                                                                reciboAtual={recibo}
                                                                irmaos={irmaos}
                                                                formatarMoeda={formatarMoeda}
                                                                formatarData={formatarData}
                                                                getValorPagoDisplay={getValorPagoDisplay}
                                                                getSaldoRestanteDisplay={getSaldoRestanteDisplay}
                                                                onVerDetalhes={verDetalhes}
                                                            />

                                                            {/* Info Fatura */}
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(4, 1fr)',
                                                                gap: '10px',
                                                                padding: '10px 14px',
                                                                background: 'rgba(79, 70, 229, 0.04)',
                                                                borderRadius: '6px',
                                                                marginBottom: '10px',
                                                                border: '1px solid rgba(79, 70, 229, 0.1)'
                                                            }}>
                                                                <div>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Fatura</div>
                                                                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#1a1a2e' }}>{faturaNumero}</div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Total Fatura</div>
                                                                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#1a1a2e' }}>{formatarMoeda(totalFatura)}</div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Método</div>
                                                                    <div style={{ fontSize: '0.6rem', fontWeight: 600, color: '#4f46e5' }}>{recibo.metodoPagamento || '---'}</div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Referência</div>
                                                                    <div style={{ fontSize: '0.6rem', fontWeight: 500, color: '#1a1a2e' }}>{recibo.referenciaPagamento || '-'}</div>
                                                                </div>
                                                            </div>

                                                            {/* Barra de progresso */}
                                                            <div style={{
                                                                padding: '10px 14px',
                                                                background: '#f8f9fa',
                                                                borderRadius: '6px',
                                                                border: '1px solid #e5e7eb',
                                                                marginBottom: '10px'
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                    <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Progresso de Pagamento</span>
                                                                    <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#059669' }}>{percentualPago.toFixed(1)}%</span>
                                                                </div>
                                                                <div style={{ width: '100%', height: '6px', background: '#e5e7eb', borderRadius: '100px', overflow: 'hidden', marginBottom: '8px' }}>
                                                                    <div style={{
                                                                        height: '100%',
                                                                        width: `${percentualPago}%`,
                                                                        background: 'linear-gradient(90deg, #10b981, #059669)',
                                                                        borderRadius: '100px',
                                                                        transition: 'width 0.3s ease'
                                                                    }}></div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Saldo Anterior</div>
                                                                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#1a1a2e' }}>{formatarMoeda(saldoAnterior)}</div>
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Total Já Pago</div>
                                                                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#059669' }}>{formatarMoeda(totalJaPago)}</div>
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Saldo Restante</div>
                                                                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: (parseFloat(recibo.saldoRestante) || 0) > 0.01 ? '#d97706' : '#059669' }}>
                                                                            {saldoRestante}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* 🔥 BOTÃO FINALIZAR na linha expandida */}
                                                            {podeFinalizar && (
                                                                <button
                                                                    onClick={() => finalizarPagamento(recibo)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '10px',
                                                                        marginBottom: '10px',
                                                                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                                        color: '#ffffff',
                                                                        border: 'none',
                                                                        borderRadius: '8px',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '8px',
                                                                        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
                                                                        transition: 'all 0.2s ease'
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(245, 158, 11, 0.4)';
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
                                                                    }}
                                                                >
                                                                    <Icon name="task_alt" size={16} />
                                                                    Finalizar Pagamento ({saldoRestante})
                                                                </button>
                                                            )}

                                                            {/* 🔥 Indicador quando fatura está totalmente paga */}
                                                            {!podeFinalizar && statusValue === 'Emitido' && (parseFloat(recibo.saldoRestante) || 0) <= 0.01 && totalFatura > 0 && (
                                                                <div style={{
                                                                    padding: '10px',
                                                                    marginBottom: '10px',
                                                                    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1), rgba(5, 150, 105, 0.05))',
                                                                    border: '1.5px solid #059669',
                                                                    borderRadius: '8px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '8px',
                                                                    color: '#059669',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700
                                                                }}>
                                                                    <Icon name="check_circle" size={16} />
                                                                    Fatura totalmente liquidada
                                                                </div>
                                                            )}

                                                            {/* Observações */}
                                                            {recibo.observacoes && (
                                                                <div style={{
                                                                    padding: '8px 12px',
                                                                    background: '#f8f9fa',
                                                                    borderLeft: '3px solid #1a3c5e',
                                                                    borderRadius: '4px',
                                                                    marginBottom: '10px'
                                                                }}>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#1a3c5e', textTransform: 'uppercase', marginBottom: '3px' }}>Observações</div>
                                                                    <div style={{ fontSize: '0.6rem', color: '#4a5568', fontStyle: 'italic' }}>{recibo.observacoes}</div>
                                                                </div>
                                                            )}

                                                            {/* Motivo da anulação */}
                                                            {recibo.status === 'Anulado' && recibo.motivoAnulacao && (
                                                                <div style={{
                                                                    padding: '8px 12px',
                                                                    background: '#fef2f2',
                                                                    borderLeft: '3px solid #dc2626',
                                                                    borderRadius: '4px'
                                                                }}>
                                                                    <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', marginBottom: '3px' }}>Motivo da Anulação</div>
                                                                    <div style={{ fontSize: '0.6rem', color: '#4a5568', fontStyle: 'italic' }}>{recibo.motivoAnulacao}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* PAGINAÇÃO */}
            {renderPagination()}

            {/* ============================================= */}
            {/* MODAL DE CRIAÇÃO (também usado para Finalizar) */}
            {/* ============================================= */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <Icon name={modoFinalizar ? "task_alt" : "add_box"} size={22} />
                                {modoFinalizar ? 'Finalizar Pagamento' : 'Novo Recibo'}
                            </h3>
                            <button className="modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
                        </div>
                        <div className="modal-body-pdf-criar">
                            <form onSubmit={handleCreate}>

                                {/* 🔥 AVISO DE MODO FINALIZAR */}
                                {modoFinalizar && (
                                    <div style={{
                                        padding: '10px 14px',
                                        background: 'rgba(245, 158, 11, 0.08)',
                                        border: '1px solid rgba(245, 158, 11, 0.25)',
                                        borderRadius: '8px',
                                        marginBottom: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        color: '#d97706'
                                    }}>
                                        <Icon name="info" size={18} />
                                        <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                            Está a finalizar um pagamento parcial.
                                            {reciboEmFinalizacao && (
                                                <> Referência ao recibo <strong>{reciboEmFinalizacao.numero}</strong>.</>
                                            )}
                                            {' '}O valor está pré-preenchido com o saldo restante.
                                        </span>
                                    </div>
                                )}

                                <div className="form-section">
                                    <h4>Dados do Recibo</h4>
                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                                            <label>Fatura <span className="required">*</span></label>
                                            <SearchInput
                                                value={searchFatura}
                                                onChange={(val) => {
                                                    setSearchFatura(val);
                                                    if (val === '') limparSelecaoFatura();
                                                }}
                                                onSelect={selecionarFatura}
                                                onClear={limparSelecaoFatura}
                                                placeholder="Buscar fatura por número ou cliente..."
                                                items={faturasPendentes}
                                                getItemLabel={(f) => `Fatura ${f.numero}`}
                                                getItemSub={(f) => f.clienteInfo?.nome || f.clienteId?.nome || 'Cliente'}
                                                getItemTag={(f) => f.status || 'Aberta'}
                                                getItemPrice={(f) => f.totalFormatado || formatarMoeda(f.total)}
                                                selectedId={formData.faturaId}
                                                iconName="receipt"
                                                iconName= "hidden"
                                                itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                                noResultsMessage="Nenhuma fatura pendente encontrada"
                                                disabled={modoFinalizar}
                                            />
                                        </div>
                                    </div>

                                    {faturaSelecionada && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: '10px',
                                            padding: '12px 14px',
                                            background: 'rgba(79, 70, 229, 0.04)',
                                            borderRadius: '8px',
                                            marginTop: '10px',
                                            border: '1px solid rgba(79, 70, 229, 0.12)'
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '0.55rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '3px' }}>Total Fatura</div>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a2e' }}>{formatarMoeda(saldoCalculado.totalFatura)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.55rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '3px' }}>Já Pago</div>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>{formatarMoeda(saldoCalculado.totalJaPago)}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.55rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: '3px' }}>Saldo Devedor</div>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#d97706' }}>{formatarMoeda(saldoCalculado.saldoDevedor)}</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-row" style={{ marginTop: '10px' }}>
                                        <div className="form-group">
                                            <label>Valor Pago <span className="required">*</span></label>
                                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                                <input
                                                    type="number"
                                                    value={formData.valorPago}
                                                    onChange={(e) => setFormData({ ...formData, valorPago: parseFloat(e.target.value) || 0 })}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    disabled={!faturaSelecionada}
                                                    style={{
                                                        flex: 1,
                                                        padding: '0.6rem 0.8rem',
                                                        border: '1.5px solid #e5e7eb',
                                                        borderRadius: '8px',
                                                        fontSize: '0.85rem',
                                                        background: !faturaSelecionada ? '#f3f4f6' : '#ffffff',
                                                        color: '#1a1a2e',
                                                        outline: 'none',
                                                        height: '42px',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                {faturaSelecionada && saldoCalculado.saldoDevedor > 0 && !modoFinalizar && (
                                                    <button
                                                        type="button"
                                                        onClick={pagarTotalidade}
                                                        style={{
                                                            padding: '0 0.8rem',
                                                            height: '42px',
                                                            border: '1.5px solid #059669',
                                                            borderRadius: '8px',
                                                            background: 'rgba(5, 150, 105, 0.08)',
                                                            color: '#059669',
                                                            fontSize: '0.65rem',
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        Totalidade
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Data de Pagamento <span className="required">*</span></label>
                                            <input
                                                type="date"
                                                value={formData.dataPagamento}
                                                onChange={(e) => setFormData({ ...formData, dataPagamento: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Método de Pagamento <span className="required">*</span></label>
                                            <select
                                                value={formData.metodoPagamento}
                                                onChange={(e) => setFormData({ ...formData, metodoPagamento: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {METODOS_PAGAMENTO.map(m => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Referência do Pagamento
                                                {formData.metodoPagamento !== 'Dinheiro' && <span className="required"> *</span>}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.referenciaPagamento}
                                                onChange={(e) => setFormData({ ...formData, referenciaPagamento: e.target.value })}
                                                placeholder={
                                                    formData.metodoPagamento === 'Transferência Bancária' ? 'Ex: TB-2024-001234' :
                                                        formData.metodoPagamento === 'Cheque' ? 'Ex: CHQ-001234 - Banco XYZ' :
                                                            formData.metodoPagamento === 'Cartão' ? 'Ex: **** 1234 - Terminal 01' :
                                                                formData.metodoPagamento === 'M-Pesa' ? 'Ex: 84 123 4567 - Código: 123456' :
                                                                    'Informe os detalhes da transação'
                                                }
                                                disabled={formData.metodoPagamento === 'Dinheiro'}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: formData.metodoPagamento === 'Dinheiro' ? '#f3f4f6' : '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                                            <label>Observações</label>
                                            <textarea
                                                value={formData.observacoes}
                                                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                                placeholder="Observações adicionais (opcional)"
                                                rows="2"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical',
                                                    minHeight: '60px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button type="submit" className="modal-btn-primary" disabled={loading || !faturaSelecionada}>
                                        {loading
                                            ? (modoFinalizar ? 'A finalizar...' : 'A criar...')
                                            : (modoFinalizar ? 'Finalizar Pagamento' : 'Criar Recibo')
                                        }
                                    </button>
                                    <button type="button" className="modal-btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================= */}
            {/* MODAL DE ANULAÇÃO */}
            {/* ============================================= */}
            {showAnularModal && reciboToAnular && (
                <div className="modal-overlay" onClick={() => setShowAnularModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">❌</span> Anular Recibo</h3>
                            <button className="modal-close" onClick={() => setShowAnularModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Confirmar Anulação</h4>
                            <p>Tem certeza que deseja anular o recibo #{reciboToAnular.numero}?</p>
                            <p className="confirm-subtext">
                                <Icon name="info" size={14} />
                                Se a fatura ficar novamente em dívida, o status será revertido.
                            </p>
                            <div className="form-group-cancelamento">
                                <label>Motivo da anulação <span className="required">*</span></label>
                                <textarea
                                    value={motivoAnulacao}
                                    onChange={(e) => setMotivoAnulacao(e.target.value)}
                                    placeholder="Informe o motivo da anulação"
                                    rows="3"
                                    style={{
                                        width: '100%',
                                        padding: '0.6rem 0.8rem',
                                        border: '1.5px solid #e5e7eb',
                                        borderRadius: '8px',
                                        fontSize: '0.85rem',
                                        background: '#ffffff',
                                        color: '#1a1a2e',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        minHeight: '70px'
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={() => anularRecibo(reciboToAnular._id)} disabled={loading}>
                                {loading ? 'A processar...' : 'Sim, Anular'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowAnularModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================= */}
            {/* MODAL DE EXCLUSÃO */}
            {/* ============================================= */}
            {showDeleteModal && reciboToDelete && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">⚠️</span> Confirmar Exclusão</h3>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Remover recibo?</h4>
                            <p>Tem certeza que deseja remover o recibo #{reciboToDelete.numero}?</p>
                            <p className="confirm-subtext">
                                <Icon name="info" size={14} /> Esta ação é irreversível.
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={handleDelete} disabled={loading}>
                                {loading ? 'A processar...' : 'Eliminar'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* =============================================
                 MODAL DE DETALHES - DUAL PANEL (PREVIEW PDF + SIDE PANEL)
                =============================================*/}
            {showDetailsModal && reciboDetalhes && (
                <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-container-dual" onClick={(e) => e.stopPropagation()}>
                        {/* PAINEL ESQUERDO - PREVIEW PDF */}
                        <div className="modal-content modal-pdf" style={{ position: 'relative', overflow: 'hidden' }}>

                            {/* WATERMARK ANULADO */}
                            {reciboDetalhes.status === 'Anulado' && (
                                <div className="watermark-overlay" style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 10,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <div className="watermark-text" style={{
                                        fontSize: '60px',
                                        fontWeight: 900,
                                        color: 'rgba(220, 38, 38, 0.12)',
                                        textTransform: 'uppercase',
                                        transform: 'rotate(-30deg)',
                                        letterSpacing: '15px',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        border: '6px solid rgba(220, 38, 38, 0.15)',
                                        padding: '20px 50px',
                                        borderRadius: '12px',
                                        background: 'rgba(220, 38, 38, 0.03)'
                                    }}>
                                        ANULADO
                                    </div>
                                </div>
                            )}

                            <div className="modal-header">
                                <h3>
                                    <Icon name="receipt_long" size={22} />
                                    Recibo {reciboDetalhes.numero}
                                </h3>
                                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                            </div>

                            {/* CONTEÚDO DO PDF */}
                            <div className="modal-body pdf-body">

                                {/* 🔥 CADEIA DE PAGAMENTOS NO MODAL */}
                                {(() => {
                                    const irmaos = getRecibosIrmaos(reciboDetalhes);
                                    if (irmaos.length === 0) return null;
                                    return (
                                        <CadeiaPagamentos
                                            reciboAtual={reciboDetalhes}
                                            irmaos={irmaos}
                                            formatarMoeda={formatarMoeda}
                                            formatarData={formatarData}
                                            getValorPagoDisplay={getValorPagoDisplay}
                                            getSaldoRestanteDisplay={getSaldoRestanteDisplay}
                                            onVerDetalhes={(r) => {
                                                setShowDetailsModal(false);
                                                setTimeout(() => verDetalhes(r), 200);
                                            }}
                                            compact={true}
                                        />
                                    );
                                })()}

                                {/* TOPO */}
                                <div className="pdf-header-top">
                                    <div className="pdf-header-left">
                                        <span className="pdf-label">Recibo</span>
                                        <span className="pdf-value">{reciboDetalhes.numero}</span>
                                    </div>
                                    <div className="pdf-header-right">
                                        <span className="pdf-label">Original</span>
                                        <span className="pdf-label">Data de Pagamento</span>
                                        <span className="pdf-value">{formatarData(reciboDetalhes.dataPagamento)}</span>
                                    </div>
                                </div>

                                {/* EMPRESA / CLIENTE */}
                                <div className="pdf-cliente-empresa-grid">
                                    <div className="pdf-empresa-section">
                                        <div className="pdf-empresa-grid">
                                            <div className="pdf-empresa-row-logo" style={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                padding: '4px 0 10px',
                                                marginTop: '-10px',
                                                width: '100%'
                                            }}>
                                                {(() => {
                                                    const empresaData = reciboDetalhes?.empresa || empresaConfig;
                                                    const logoUrl = empresaData?.imagemPerfil || null;
                                                    const nomeEmpresa = empresaData?.nome || '---';

                                                    if (logoUrl && logoUrl !== 'null' && logoUrl !== 'undefined' && logoUrl !== '') {
                                                        return (
                                                            <img
                                                                src={logoUrl}
                                                                alt={nomeEmpresa}
                                                                className="pdf-empresa-logo"
                                                                style={{ maxHeight: '70px', maxWidth: '280px', objectFit: 'contain', display: 'block', marginRight: 'auto', marginLeft: '-70px' }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const parent = e.target.parentElement;
                                                                    if (parent) {
                                                                        parent.innerHTML = `<span class="pdf-empresa-value" style="font-size:16px;font-weight:bold;color:#1a3a5c;">${nomeEmpresa}</span>`;
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <span className="pdf-empresa-value" style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a3a5c', marginRight: 'auto' }}>
                                                            {nomeEmpresa}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value">{reciboDetalhes?.empresa?.nome || empresaConfig.nome || '---'}</span>
                                            </div>
                                            <div className="pdf-empresa-row2">
                                                <span className="pdf-empresa-value2">
                                                    {reciboDetalhes?.empresa?.morada?.enderecoCompleto || empresaConfig.morada?.enderecoCompleto || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row3">
                                               <span style={{ fontWeight: 'bold', fontSize: '8px' }}>NUIT : </span> <span className="pdf-empresa-value3">{reciboDetalhes?.empresa?.nuit || empresaConfig.nuit || '---'}</span>
                                            </div>
                                            <div className="pdf-empresa-row4">
                                                <span className="pdf-empresa-value4">{reciboDetalhes?.empresa?.email || empresaConfig.email || '---'}</span>
                                            </div>
                                            <div className="pdf-empresa-row5">
                                                <span className="pdf-empresa-value5">
                                                    {(() => {
                                                        const website = reciboDetalhes?.empresa?.website || empresaConfig.website || '---';
                                                        return website.replace(/^https?:\/\//, '').replace(/\/$/, '') || '---';
                                                    })()}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row6">
                                                <span className="pdf-empresa-value6">{reciboDetalhes?.empresa?.telefone || empresaConfig.telefone || '---'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pdf-cliente-section">
                                        <div className="pdf-cliente-grid">
                                            <div className="pdf-cliente-row" style={{ marginTop: '50px' }}></div>
                                            <div className="pdf-cliente-row-nome">
                                                <span className="pdf-cliente-value" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                                    {getClienteNome(reciboDetalhes)}
                                                </span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value" style={{ fontSize: '8px' }}>
                                                    {reciboDetalhes.faturaSnapshot?.cliente?.endereco || reciboDetalhes.clienteId?.endereco || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-cliente-row" style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                                <span style={{ fontSize: '8px', fontWeight: 700 }}>NUIT :</span>
                                                <span className="pdf-cliente-value" style={{ fontSize: '8px' }}>
                                                    {getClienteNif(reciboDetalhes) || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value" style={{ fontSize: '8px' }}>
                                                    {reciboDetalhes.faturaSnapshot?.cliente?.telefone || reciboDetalhes.clienteId?.telefone || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value" style={{ fontSize: '8px' }}>
                                                    {reciboDetalhes.faturaSnapshot?.cliente?.email || reciboDetalhes.clienteId?.email || '---'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* PROCESSADO POR COMPUTADOR */}
                                <div className="pdf-processado" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '4px 0 8px',
                                    borderBottom: '1px solid #e9ecef',
                                    marginBottom: '10px'
                                }}>
                                    <span className="pdf-empresa-label" style={{ fontWeight: 500, color: '#6c757d', fontSize: '8px', textTransform: 'uppercase' }}>
                                        Processado por computador
                                    </span>
                                    {(reciboDetalhes?.empresa?.cafe || empresaConfig.cafe) && (
                                        <span className="pdf-cafe-code" style={{
                                            fontSize: '8.5px',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontFamily: 'Courier New, monospace',
                                            background: 'Grey',
                                            padding: '2px 10px',
                                            borderRadius: '4px'
                                        }}>
                                            CAFE: {reciboDetalhes?.empresa?.cafe || empresaConfig.cafe || '---'}
                                        </span>
                                    )}
                                </div>

                                {/* CAIXA VALOR PAGO */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto',
                                    gap: '20px',
                                    alignItems: 'center',
                                    padding: '16px 22px',
                                    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.08), rgba(5, 150, 105, 0.03))',
                                    border: '2px solid #059669',
                                    borderRadius: '10px',
                                    marginBottom: '14px'
                                }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ fontSize: '9px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                            Valor Recebido
                                        </span>
                                        <span style={{ fontSize: '9px', color: '#4a5568', fontWeight: 500 }}>
                                            <strong style={{ color: '#1a1a1a', fontWeight: 700 }}>{reciboDetalhes.metodoPagamento}</strong>
                                            {reciboDetalhes.referenciaPagamento && ` - ${reciboDetalhes.referenciaPagamento}`}
                                        </span>
                                        <span style={{ fontSize: '8px', color: '#4a5568', fontWeight: 600 }}>
                                            {reciboDetalhes.tipoPagamento === 'Total' ? '✓ Pagamento Total' : '⏳ Pagamento Parcial'}
                                        </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <span style={{ fontSize: '26px', fontWeight: 800, color: '#059669', lineHeight: 1, letterSpacing: '-1px' }}>
                                            {reciboDetalhes.valorPagoFormatado || formatarMoeda(reciboDetalhes.valorPago)}
                                        </span>
                                    </div>
                                </div>

                                {/* BARRA DE PROGRESSO */}
                                {(() => {
                                    const snap = reciboDetalhes.faturaSnapshot || {};
                                    const totalFatura = parseFloat(snap.total) || 0;
                                    const saldoRestante = parseFloat(reciboDetalhes.saldoRestante) || 0;
                                    const saldoAnterior = parseFloat(reciboDetalhes.saldoAnterior) || 0;
                                    const totalJaPago = Math.max(0, totalFatura - saldoRestante);
                                    const percentualPago = totalFatura > 0
                                        ? Math.min(100, (totalJaPago / totalFatura) * 100)
                                        : 0;

                                    return (
                                        <div style={{
                                            padding: '10px 14px',
                                            background: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            marginBottom: '14px'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '8px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Progresso de Pagamento da Fatura
                                                </span>
                                                <span style={{ fontSize: '10px', fontWeight: 800, color: '#059669' }}>
                                                    {percentualPago.toFixed(1)}%
                                                </span>
                                            </div>
                                            <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '100px', overflow: 'hidden', marginBottom: '6px' }}>
                                                <div style={{
                                                    height: '100%',
                                                    width: `${percentualPago}%`,
                                                    background: 'linear-gradient(90deg, #10b981, #059669)',
                                                    borderRadius: '100px'
                                                }}></div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '4px' }}>
                                                <div>
                                                    <div style={{ fontSize: '7px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase' }}>Total Fatura</div>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#1a1a1a' }}>
                                                        {formatarMoeda(totalFatura)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '7px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase' }}>Total Já Pago</div>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#059669' }}>
                                                        {formatarMoeda(totalJaPago)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '7px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase' }}>Saldo Restante</div>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, color: saldoRestante > 0.01 ? 'red' : '#059669' }}>
                                                        {formatarMoeda(saldoRestante)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* REFERENTE À FATURA */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '12px',
                                    padding: '10px 14px',
                                    background: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #e9ecef',
                                    marginBottom: '14px'
                                }}>
                                    <div>
                                        <div style={{ fontSize: '8px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', marginBottom: '2px' }}>Fatura Nº</div>
                                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#1a3c5e', fontFamily: 'Courier New, monospace' }}>
                                            {reciboDetalhes.faturaSnapshot?.numero || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '8px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', marginBottom: '2px' }}>Data de Emissão</div>
                                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#1a1a1a' }}>
                                            {formatarData(reciboDetalhes.faturaSnapshot?.dataEmissao)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '8px', fontWeight: 700, color: '#4a5568', textTransform: 'uppercase', marginBottom: '2px' }}>Moeda</div>
                                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#1a1a1a' }}>
                                            {reciboDetalhes.moeda || empresaConfig.moeda?.codigo || 'MZN'}
                                        </div>
                                    </div>
                                </div>

                                {/* RESUMO DA FATURA */}
                                <div style={{
                                    padding: '12px 16px',
                                    background: '#fafbfc',
                                    border: '1px solid #d0d7de',
                                    borderRadius: '6px',
                                    marginBottom: '14px'
                                }}>
                                    <div style={{
                                        fontSize: '9px',
                                        fontWeight: 800,
                                        color: '#1a3a5c',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderBottom: '1px solid #d0d7de',
                                        paddingBottom: '6px',
                                        marginBottom: '8px'
                                    }}>
                                        Resumo da Fatura Liquidada
                                    </div>
                                           
                                        <div style={{
                                            gridColumn: '1 / -1',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            padding: '6px 0 0 0',
                                            fontSize: '13px',
                                            borderTop: '0px solid #1a3c5e',
                                            marginTop: '4px'
                                        }}>
                                            <span style={{ color: '#1a3c5e', fontWeight: 800, fontSize: '11px' }}>Total da Fatura</span>
                                            <span style={{ color: '#1a3c5e', fontWeight: 800 }}>
                                                {formatarMoeda(reciboDetalhes.faturaSnapshot?.total || 0)}
                                            </span>
                                        </div>
                                    </div>
                                

                                {/* OBSERVAÇÕES */}
                                {reciboDetalhes.observacoes && (
                                    <div style={{
                                        padding: '8px 12px',
                                        background: '#f8f9fa',
                                        borderLeft: '3px solid #1a3c5e',
                                        borderRadius: '4px',
                                        marginBottom: '14px'
                                    }}>
                                        <div style={{ fontSize: '8px', fontWeight: 700, color: '#1a3c5e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>
                                            Observações
                                        </div>
                                        <div style={{ fontSize: '9px', color: '#4a5568', lineHeight: 1.5, fontStyle: 'italic' }}>
                                            {reciboDetalhes.observacoes}
                                        </div>
                                    </div>
                                )}

                                {/* FOOTER SIMPLIFICADO */}
                                <div className="pdf-footer-bottom" style={{ borderTop: '2px solid #1a3a5c', paddingTop: '12px', marginTop: '4px' }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '4px 20px',
                                        padding: '6px 0',
                                        fontSize: '9px',
                                        color: '#6c757d'
                                    }}>
                                        <div style={{ textAlign: 'left' }}>
                                            <span>Documento emitido pelo Software RAF ERP</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span>Utilizador: <strong>{reciboDetalhes.criadoPor?.nome || '---'}</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PAINEL DIREITO - SIDE PANEL */}
                        <div className="modal-side-panel">
                            <div className="side-panel-header">
                                <h4>
                                    <Icon name="settings" size={18} />
                                    Ações
                                </h4>
                            </div>

                            <div className="side-panel-body">
                                <div className="side-panel-status">
                                    <span className="status-label">Status</span>
                                    <span className={`status-badge ${getStatusClass(reciboDetalhes)}`}>
                                        <Icon name={getStatusIcon(reciboDetalhes)} size={12} />
                                        {getStatusFormatado(reciboDetalhes)}
                                    </span>
                                </div>

                                {reciboDetalhes.status === 'Anulado' && reciboDetalhes.motivoAnulacao && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <div className="side-panel-cancel-motivo" style={{
                                            background: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            borderRadius: '8px',
                                            padding: '12px 14px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                <Icon name="cancel" size={16} color="#dc2626" />
                                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase' }}>
                                                    Motivo da Anulação
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#1a1a2e', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                {reciboDetalhes.motivoAnulacao}
                                            </p>
                                        </div>
                                    </>
                                )}

                                {/* 🔥 BOTÃO FINALIZAR no side panel */}
                                {podeFinalizarPagamento(reciboDetalhes) && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <button
                                            className="side-panel-btn"
                                            onClick={() => {
                                                finalizarPagamento(reciboDetalhes);
                                                setShowDetailsModal(false);
                                            }}
                                            style={{
                                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                color: '#ffffff',
                                                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                                            }}
                                        >
                                            <Icon name="task_alt" size={18} />
                                            <span>Finalizar Pagamento</span>
                                        </button>
                                    </>
                                )}

                                <div className="side-panel-divider"></div>

                                {/* BAIXAR PDF */}
                                <button
                                    className="side-panel-btn btn-pdf"
                                    onClick={async () => {
                                        if (reciboDetalhes.status === 'Anulado') {
                                            showToast('Este recibo está anulado e não pode ser exportado', 'warning');
                                            return;
                                        }
                                        try {
                                            setLoading(true);
                                            const token = localStorage.getItem('token');
                                            if (!token) {
                                                showToast('Faça login novamente', 'danger');
                                                setLoading(false);
                                                return;
                                            }

                                            const response = await fetch(
                                                `/api/recibos/${reciboDetalhes._id}/pdf`,
                                                { headers: { 'Authorization': `Bearer ${token}` } }
                                            );

                                            if (!response.ok) {
                                                const errorText = await response.text();
                                                throw new Error(`Erro ${response.status}: ${errorText}`);
                                            }

                                            const blob = await response.blob();
                                            const nomeArquivo = `Recibo_${reciboDetalhes.numero}_${getClienteNome(reciboDetalhes)}.pdf`;

                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', nomeArquivo);
                                            document.body.appendChild(link);
                                            link.click();

                                            setTimeout(() => {
                                                link.parentNode.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                            }, 100);

                                            showToast('PDF baixado com sucesso!', 'success');
                                        } catch (error) {
                                            console.error('❌ Erro ao baixar PDF:', error);
                                            showToast(error.message || 'Erro ao gerar PDF', 'danger');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || reciboDetalhes.status === 'Anulado'}
                                    style={{
                                        opacity: reciboDetalhes.status === 'Anulado' ? 0.5 : 1,
                                        cursor: reciboDetalhes.status === 'Anulado' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner">⏳</span>
                                            <span>Gerando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="picture_as_pdf" size={18} />
                                            <span>Baixar PDF</span>
                                        </>
                                    )}
                                </button>

                                {/* IMPRIMIR */}
                                <button
                                    className="side-panel-btn btn-print"
                                    onClick={async () => {
                                        if (reciboDetalhes.status === 'Anulado') {
                                            showToast('Este recibo está anulado e não pode ser impresso', 'warning');
                                            return;
                                        }
                                        try {
                                            setLoading(true);
                                            const token = localStorage.getItem('token');
                                            if (!token) {
                                                showToast('Faça login novamente', 'danger');
                                                setLoading(false);
                                                return;
                                            }

                                            const response = await fetch(
                                                `/api/recibos/${reciboDetalhes._id}/pdf`,
                                                { headers: { 'Authorization': `Bearer ${token}` } }
                                            );

                                            if (!response.ok) throw new Error(`Erro ${response.status}`);

                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const printWindow = window.open(url, '_blank');

                                            if (!printWindow) {
                                                showToast('Por favor, permita pop-ups para imprimir', 'warning');
                                                window.URL.revokeObjectURL(url);
                                                setLoading(false);
                                                return;
                                            }

                                            printWindow.onload = function () {
                                                setTimeout(() => {
                                                    printWindow.print();
                                                }, 500);
                                            };

                                            showToast('Documento enviado para impressão', 'success');
                                        } catch (error) {
                                            console.error('❌ Erro ao imprimir:', error);
                                            showToast(error.message || 'Erro ao imprimir', 'danger');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || reciboDetalhes.status === 'Anulado'}
                                    style={{
                                        opacity: reciboDetalhes.status === 'Anulado' ? 0.5 : 1,
                                        cursor: reciboDetalhes.status === 'Anulado' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner">⏳</span>
                                            <span>A preparar...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="print" size={18} />
                                            <span>Imprimir</span>
                                        </>
                                    )}
                                </button>

                                {reciboDetalhes.status !== 'Anulado' && (
                                    <button
                                        className="side-panel-btn btn-cancel-action"
                                        onClick={() => {
                                            setReciboToAnular(reciboDetalhes);
                                            setShowDetailsModal(false);
                                            setShowAnularModal(true);
                                        }}
                                    >
                                        <Icon name="cancel" size={18} />
                                        <span>Anular Recibo</span>
                                    </button>
                                )}

                                <div className="side-panel-divider"></div>

                                <button
                                    className="side-panel-btn btn-close"
                                    onClick={() => setShowDetailsModal(false)}
                                >
                                    <Icon name="close" size={18} />
                                    <span>Fechar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toast
                message={toast.message}
                type={toast.type}
                onClose={closeToast}
            />
        </div>
    );
}